import { FormControlConstants, type WhenOperationModel } from '@generated';
import type { FormValues } from '~/shared/forms/types';

export function satisifiesWhenConditions(
	whens: WhenOperationModel[],
	values: FormValues,
	whenCondition: string = FormControlConstants.whenConditionOperators.or,
	changedFields: Set<string> | null = null
): boolean {
	return whens.reduce((result, rule) => {
		return whenCondition === FormControlConstants.whenConditionOperators.and
			? result && satisfiesWhenCondition(rule, values, changedFields)
			: result || satisfiesWhenCondition(rule, values, changedFields);
	}, whenCondition === FormControlConstants.whenConditionOperators.and);
}

export function satisfiesWhenCondition(when: WhenOperationModel, values: FormValues, changedFields: Set<string> | null = null): boolean {
	const { name, op } = when;

	if (op === FormControlConstants.whenOperations.changes) {
		return changedFields !== null && name.split('|').some(fieldName => changedFields.has(fieldName));
	}
	if (op === FormControlConstants.whenOperations.expression) {
		return evaluateExression(when.value!, values);
	}
	if (name.includes('|')) {
		return name.split('|').some(x => satisfiesWhenCondition({ ...when, name: x }, values, changedFields));
	}

	const currentFieldValue = values[name];
	if (op === FormControlConstants.whenOperations.isTrue) {
		return currentFieldValue === true;
	} else if (op === FormControlConstants.whenOperations.isFalse) {
		return currentFieldValue === false;
	} else if (op === FormControlConstants.whenOperations.isEmpty) {
		return !currentFieldValue || (Array.isArray(currentFieldValue) && currentFieldValue.length === 0);
	} else if (op === FormControlConstants.whenOperations.isNotEmpty) {
		return currentFieldValue && (!Array.isArray(currentFieldValue) || (Array.isArray(currentFieldValue) && currentFieldValue.length > 0));
	}

	if (!when.value) {
		throw new Error(`Value is required for when operation ${op}`);
	}

	const fieldValueOrValuesToTest = when.value.includes('|') ? when.value.split(FormControlConstants.formValidationOperators.or) : [when.value];
	if (op === FormControlConstants.whenOperations.contains) {
		return currentFieldValue && Array.isArray(currentFieldValue) && fieldValueOrValuesToTest.some(value => currentFieldValue.includes(value));
	} else if (op === FormControlConstants.whenOperations.doesNotContain) {
		return !currentFieldValue || !Array.isArray(currentFieldValue) || fieldValueOrValuesToTest.every(value => !currentFieldValue.includes(value));
	} else if (op === FormControlConstants.whenOperations.equals) {
		// eslint-disable-next-line eqeqeq
		return fieldValueOrValuesToTest.some(value => currentFieldValue == value);
	} else if (op === FormControlConstants.whenOperations.doesNotEqual) {
		// eslint-disable-next-line eqeqeq
		return fieldValueOrValuesToTest.every(value => currentFieldValue != value);
	}

	throw new Error(`Unsupported when operation: ${op}`);
}

export function evaluateExression(expression: string, values: Record<string, unknown>, test?: boolean): boolean {
	const keys = Object.keys(values);
	const valuesAsObject = Object.values(values);
	const fn = new Function(...keys, `return (${expression});`);
	return fn(...valuesAsObject);
}
